import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cx, type ButtonSize } from '@/components/ui';
import { useCancelPendingDeposit } from '@/features/finance/hooks/useCancelPendingDeposit';

export interface CancelDepositControlProps {
  /** Pending wallet deposit to cancel. */
  transactionId: string;
  /** Called after the server confirmed the cancel (the app-wide event has already fired). */
  onCancelled?: () => void;
  size?: ButtonSize;
  /** Render the first step as a text link instead of a full-width button. */
  quiet?: boolean;
  className?: string;
}

/**
 * "Cancel deposit" with an inline confirm step. Used wherever a pending wallet deposit is shown:
 * Activity detail, the payment modal, the pending-payment floater and the deposit checkout.
 */
export default function CancelDepositControl({
  transactionId,
  onCancelled,
  size = 'md',
  quiet = false,
  className,
}: CancelDepositControlProps) {
  const { t } = useTranslation();
  const { cancelDeposit, cancellingId } = useCancelPendingDeposit();
  const [confirming, setConfirming] = useState(false);
  const busy = cancellingId === transactionId;

  useEffect(() => {
    setConfirming(false);
  }, [transactionId]);

  if (!confirming) {
    return (
      <Button
        variant={quiet ? 'ghost' : 'secondary'}
        size={size}
        fullWidth={!quiet}
        className={className}
        onClick={() => setConfirming(true)}
        data-testid="cancel-deposit"
      >
        {t('transactions.cancelDeposit', 'Cancel deposit')}
      </Button>
    );
  }

  const confirm = async () => {
    const ok = await cancelDeposit(transactionId);
    if (ok) onCancelled?.();
    else setConfirming(false);
  };

  return (
    <div
      className={cx('flex flex-col gap-2 rounded-control border border-danger/30 bg-danger/10 p-3', className)}
      role="group"
      data-testid="cancel-deposit-confirm"
    >
      <p className="m-0 font-ui text-small text-primary">
        {t('transactions.cancelDepositConfirm', 'Cancel this deposit? You can start a new one anytime.')}
      </p>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" fullWidth disabled={busy} onClick={() => setConfirming(false)}>
          {t('transactions.cancelDepositKeep', 'Keep waiting')}
        </Button>
        <Button
          variant="danger"
          size="sm"
          fullWidth
          disabled={busy}
          onClick={() => void confirm()}
          data-testid="cancel-deposit-yes"
        >
          {busy
            ? t('common.pleaseWait', 'Please wait…')
            : t('transactions.cancelDepositConfirmYes', 'Yes, cancel deposit')}
        </Button>
      </div>
    </div>
  );
}
